import { useWebBlocksClient } from "../../blocks/client"
import {
  BlocksContentType,
  BlockType,
  useBlocksContentList
} from "@pathwright/blocks-engine"
import { htmlToText } from "@pathwright/utils/src/text/htmlToText"
import { IndexableItem } from "../../utils/search-normalize"
import { SearchableBlockType } from "./types"

// Utilities to make Blocks indexable and searchable in the Path Navigator

const getFirstLineAsTitle = (text?: string) => {
  return text?.split("\n")[0] || ""
}

const removeFirstLine = (text?: string) => {
  if (!text) return ""
  const lines = text.split("\n")
  return lines.slice(1).join("\n").trim() // Remove first line and trim
}

const parseMarkdown = (block: SearchableBlockType) => {
  const { data } = block
  const textContent = htmlToText(data.html)
  const title = getFirstLineAsTitle(textContent)
  const content = removeFirstLine(textContent)

  return {
    title,
    description: "",
    content // Ensure we don’t include the first line
  }
}

const parseQuote = (block: SearchableBlockType) => {
  const { data } = block

  const quoteAsText = htmlToText(data.quote)
  const attributionAsText = htmlToText(data.attribution)

  const firstLine = getFirstLineAsTitle(quoteAsText)
  const restQuote = removeFirstLine(quoteAsText)

  const title = attributionAsText ? attributionAsText : firstLine
  const description = attributionAsText ? firstLine : restQuote
  const content = attributionAsText ? restQuote : ""

  return {
    title,
    description,
    content
  }
}

const parseText = (block: SearchableBlockType) => {
  const { data } = block

  let content = data.html
  // Merge aside and content (2 column text)
  if (data.aside) {
    content += `\n${data.aside}`
  }

  const textContent = htmlToText(content)
  const title = getFirstLineAsTitle(textContent)
  const cleanContent = removeFirstLine(textContent)

  return {
    title,
    description: "",
    content: content ? cleanContent : ""
  }
}

const parseTitle = (block: SearchableBlockType) => {
  const { data } = block

  const title = htmlToText(data.title)

  return {
    title,
    description: "",
    content: ""
  }
}

const parseAudio = (block: SearchableBlockType) => {
  const { data } = block

  const title = htmlToText(data.name)

  return {
    title,
    description: "",
    content: ""
  }
}

const parseAttachments = (block: SearchableBlockType) => {
  const { data } = block

  if (!data.files || data.files.length === 0) {
    return null
  }

  const titles = data.files.map((file: any) => htmlToText(file.name)).join(", ")

  return {
    title: titles,
    description: "",
    content: ""
  }
}

const parseQuestion = (block: SearchableBlockType) => {
  const { data } = block

  const { question, description, options } = data

  return {
    title: htmlToText(question),
    description: htmlToText(description),
    content: options?.length
      ? htmlToText(
          options
            .map((option: any) => {
              return option.label
            })
            .join(", ")
        )
      : ""
  }
}

const parseSubmit = (block: SearchableBlockType) => {
  const { data } = block

  const title = htmlToText(data.submitPrompt)

  return {
    title,
    description: "",
    content: ""
  }
}

const parseButton = (block: SearchableBlockType) => {
  const { data } = block

  const title = htmlToText(data.buttonLabel)
  const description = htmlToText(data.link)
  const content = htmlToText(data.description)

  return {
    title,
    description,
    content
  }
}

const parseAnnotatedAssessment = (block: SearchableBlockType) => {
  const { data } = block

  const title = htmlToText(data.question)
  const description = htmlToText(data.description)

  return {
    title,
    description,
    content: ""
  }
}

const parseTasklist = (block: SearchableBlockType) => {
  const { data } = block

  const title = htmlToText(data.title)
  const description = htmlToText(data.description)
  const tasks = data.tasks

  return {
    title,
    description,
    content: tasks?.length
      ? htmlToText(
          tasks
            .map((task: any) => {
              return task.content
            })
            .join(", ")
        )
      : ""
  }
}

const parseCards = (block: SearchableBlockType) => {
  const { data } = block

  const cards = data.cards

  if (!cards || cards.length === 0) return null

  return {
    title: htmlToText(cards.map((card: any) => card.title).join(", ")),
    description: "",
    content: htmlToText(cards.map((card: any) => card.html).join(", "))
  }
}

const parseFlashcards = (block: SearchableBlockType) => {
  const { data } = block

  const cards = data.flashcards

  if (!cards || cards.length === 0) return null

  return {
    title: htmlToText(cards.map((card: any) => card.term?.html).join(", ")),
    description: "",
    content: htmlToText(
      cards.map((card: any) => card.defintion?.html).join(", ")
    )
  }
}

const parseImage = (block: SearchableBlockType) => {
  const { data } = block

  if (!data.text && !data.annotations) return null

  const title = htmlToText(data.text)

  return {
    title,
    description: "",
    content: data?.annotations
      ? htmlToText(
          data.annotations.map((annotation: any) => annotation.html).join(", ")
        )
      : ""
  }
}

type IndexableBlock = Omit<IndexableItem, "id" | "type">

export const getIndexableBlock = (
  block: SearchableBlockType
): IndexableBlock | null => {
  switch (block.type) {
    case "markdown":
      return parseMarkdown(block) as IndexableBlock
    case "quote":
      return parseQuote(block) as IndexableBlock
    case "text":
      return parseText(block) as IndexableBlock
    case "title":
      return parseTitle(block) as IndexableBlock
    case "audio":
      return parseAudio(block) as IndexableBlock
    case "attachments":
      return parseAttachments(block) as IndexableBlock
    case "question":
      return parseQuestion(block) as IndexableBlock
    case "submit":
      return parseSubmit(block) as IndexableBlock
    case "meeting":
    case "button":
      return parseButton(block) as IndexableBlock
    case "annotatedassessment":
      return parseAnnotatedAssessment(block) as IndexableBlock
    case "tasklist":
      return parseTasklist(block) as IndexableBlock
    case "cards":
      return parseCards(block) as IndexableBlock
    case "flashcards":
      return parseFlashcards(block) as IndexableBlock
    case "image":
      return parseImage(block) as IndexableBlock

    default:
      return null
  }
}

export const flattenPathBlocks = (contentList: BlocksContentType[]) => {
  return contentList.reduce((acc, content) => {
    return acc.concat(
      content.blocks.map(
        (block: BlockType) =>
          ({
            ...block,
            contextKey: content.contextKey
          } as SearchableBlockType)
      )
    )
  }, [] as SearchableBlockType[])
}

export const useBlocksContent = ({
  contextKeys,
  pathMode,
  skip
}: {
  contextKeys: string[]
  pathMode: number
  skip: boolean
}) => {
  const blocksClient = useWebBlocksClient()

  return useBlocksContentList({
    variables: {
      contextKeys,
      draft: pathMode >= 15,
      mode: "VIEW"
    },
    skip,
    client: blocksClient || undefined
  })
}
